import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { differenceInDays } from 'date-fns';

interface Message {
  id: string;
  title: string;
  body: string;
  message_type: string;
  icon: string;
  button_text: string;
  button_action: string | null;
  cover_image: string | null;
  language: string;
}

interface _Trigger {
  id: string;
  message_id: string;
  trigger_type: string;
  trigger_config: Record<string, number>;
  priority: number | null;
  delay_seconds: number | null;
  show_once: boolean | null;
}

interface UserContext {
  userId: string;
  userName?: string;
  quitDate?: string;
  cigarettesPerDay?: number;
  costPerPack?: number;
  language?: string;
  daysSmokeFree?: number;
  moneySaved?: number;
  cigarettesAvoided?: number;
  programProgress?: number;
  currentPhase?: string;
  productId?: string;
}

interface UsePopupMessagesReturn {
  currentMessage: Message | null;
  showMessage: boolean;
  dismissMessage: () => Promise<void>;
  checkTrigger: (triggerType: string, context?: Record<string, number>) => Promise<void>;
}

export function usePopupMessages(userContext: UserContext | null): UsePopupMessagesReturn {
  const [currentMessage, setCurrentMessage] = useState<Message | null>(null);
  const [showMessage, setShowMessage] = useState(false);
  const [shownMessageIds, setShownMessageIds] = useState<Set<string>>(new Set());
  const messageQueue = useRef<{ message: Message; delay: number }[]>([]);
  const isProcessing = useRef(false);

  // Load user's message history
  useEffect(() => {
    if (!userContext?.userId) return;

    const loadHistory = async () => {
      const { data } = await supabase
        .from('user_message_history')
        .select('message_id')
        .eq('user_id', userContext.userId);

      if (data) {
        setShownMessageIds(new Set(data.map(d => d.message_id)));
      }
    };

    loadHistory();
  }, [userContext?.userId]);

  const interpolateVariables = useCallback((text: string, context: UserContext): string => {
    return text
      .replace(/\{\{user_name\}\}/g, context.userName || 'there')
      .replace(/\{\{days_smoke_free\}\}/g, String(context.daysSmokeFree || 0))
      .replace(/\{\{money_saved\}\}/g, `$${(context.moneySaved || 0).toFixed(2)}`)
      .replace(/\{\{cigarettes_avoided\}\}/g, String(context.cigarettesAvoided || 0))
      .replace(/\{\{current_phase\}\}/g, context.currentPhase || 'Getting Started')
      .replace(/\{\{progress_percent\}\}/g, `${context.programProgress || 0}%`);
  }, []);

  const processQueue = useCallback(async () => {
    if (isProcessing.current || messageQueue.current.length === 0) return;

    isProcessing.current = true;
    const { message, delay } = messageQueue.current.shift()!;

    if (delay > 0) {
      await new Promise(resolve => setTimeout(resolve, delay * 1000));
    }

    setCurrentMessage(message);
    setShowMessage(true);
    isProcessing.current = false;
  }, []);

  const dismissMessage = useCallback(async () => {
    if (!currentMessage || !userContext?.userId) {
      setShowMessage(false);
      setCurrentMessage(null);
      return;
    }

    // Record in history
    await supabase.from('user_message_history').insert({
      user_id: userContext.userId,
      message_id: currentMessage.id,
      action_taken: 'dismissed',
    });

    setShownMessageIds(prev => new Set([...prev, currentMessage.id]));
    setShowMessage(false);
    setCurrentMessage(null);

    // Process next in queue
    setTimeout(processQueue, 500);
  }, [currentMessage, userContext?.userId, processQueue]);

  const checkTrigger = useCallback(async (triggerType: string, context?: Record<string, number>) => {
    if (!userContext?.userId) return;

    // Get triggers matching the type
    let query = supabase
      .from('message_triggers')
      .select('*, popup_messages(*)')
      .eq('trigger_type', triggerType)
      .eq('is_active', true);

    // Filter by product: show messages where product_id matches current product, is 'all', or is NULL
    if (userContext.productId) {
      query = query.or(`product_id.eq.${userContext.productId},product_id.eq.all,product_id.is.null`, {
        foreignTable: 'popup_messages'
      });
    }

    const { data: triggers } = await query.order('priority', { ascending: false });

    if (!triggers || triggers.length === 0) return;

    for (const trigger of triggers) {
      const message = trigger.popup_messages as unknown as Message;

      // Skip if message doesn't exist
      if (!message) continue;

      // Skip if already shown and show_once is true
      if (trigger.show_once && shownMessageIds.has(message.id)) continue;

      // Skip if language doesn't match
      if (message.language !== (userContext?.language || 'en')) continue;

      // Check trigger conditions
      const config = trigger.trigger_config as Record<string, number>;
      let conditionMet = true;

      switch (triggerType) {
        case 'smoke_free_days':
          conditionMet = (userContext.daysSmokeFree || 0) >= (config.days || 0);
          break;
        case 'money_saved':
          conditionMet = (userContext.moneySaved || 0) >= (config.amount || 0);
          break;
        case 'cigarettes_avoided':
          conditionMet = (userContext.cigarettesAvoided || 0) >= (config.count || 0);
          break;
        case 'program_progress':
          conditionMet = (userContext.programProgress || 0) >= (config.percent || 0);
          break;
        case 'phase_completed':
          conditionMet = (context?.phase_index || 0) >= (config.phase_index || 0);
          break;
        case 'activity_completed':
          conditionMet = (context?.count || 0) >= (config.count || 1);
          break;
        case 'high_intensity_craving':
          conditionMet = (context?.intensity || 0) >= (config.intensity_gte || 8);
          break;
        case 'low_mood':
          conditionMet = (context?.rating || 10) <= (config.rating_lte || 3);
          break;
        case 'streak_days':
          conditionMet = (context?.days || 0) >= (config.days || 7);
          break;
        case 'inactive_days':
          conditionMet = (context?.days || 0) >= (config.days || 3);
          break;
        case 'stress_logged':
        case 'anxiety_logged':
          // Simple acknowledgment triggers - no conditions
          conditionMet = true;
          break;
        case 'high_stress_logged':
        case 'high_anxiety_logged':
          // High level support triggers - check if level >= threshold
          conditionMet = (context?.level || 0) >= (config.level_gte || 7);
          break;
        // Simple triggers with no config
        case 'first_login':
        case 'profile_completed':
        case 'quit_date_set':
        case 'craving_logged':
        case 'mood_logged':
        case 'cigarette_logged':
        case 'program_started':
        case 'booking_completed':
          conditionMet = true;
          break;
        default:
          conditionMet = false;
      }

      if (conditionMet) {
        // Interpolate variables in the message
        const interpolatedMessage: Message = {
          ...message,
          title: interpolateVariables(message.title, userContext),
          body: interpolateVariables(message.body, userContext),
        };

        // Check if message is already in queue
        const alreadyQueued = messageQueue.current.some(q => q.message.id === message.id);
        if (alreadyQueued) continue;

        messageQueue.current.push({
          message: interpolatedMessage,
          delay: trigger.delay_seconds ?? 0,
        });

        // Mark as shown if show_once (do this immediately to prevent duplicates)
        if (trigger.show_once) {
          setShownMessageIds(prev => new Set([...prev, message.id]));
        }
      }
    }

    // Start processing queue
    processQueue();
  }, [userContext, shownMessageIds, interpolateVariables, processQueue]);

  return {
    currentMessage,
    showMessage,
    dismissMessage,
    checkTrigger,
  };
}

// Helper to calculate user stats
export function calculateUserStats(profile: {
  quit_date?: string | null;
  cigarettes_per_day?: number | null;
  cost_per_pack?: number | null;
}) {
  if (!profile.quit_date) {
    return { daysSmokeFree: 0, moneySaved: 0, cigarettesAvoided: 0 };
  }

  const daysSmokeFree = Math.max(0, differenceInDays(new Date(), new Date(profile.quit_date)));
  const cigarettesPerDay = profile.cigarettes_per_day || 0;
  const costPerPack = profile.cost_per_pack || 10;
  const cigarettesPerPack = 20;

  const cigarettesAvoided = daysSmokeFree * cigarettesPerDay;
  const packsAvoided = cigarettesAvoided / cigarettesPerPack;
  const moneySaved = packsAvoided * costPerPack;

  return { daysSmokeFree, moneySaved, cigarettesAvoided };
}
